/* eslint-disable jsx-a11y/label-has-associated-control */
import { Select, SelectMultiple, ThemeContext } from 'grommet';
import { useId, useState } from 'preact/hooks';

import { GradeNumber, ISOAlpha2Code } from '@/redux/slices/user/types';
import { COUNTRIES, COUNTRY_TO_ISO, ISO_TO_COUNTRY } from '@/shared/countries';
import {
  LABEL_TO_TEACHING_TYPE,
  LEVEL_OPTIONS,
  LEVEL_TO_NUMBER,
  NUMBER_TO_LEVEL,
  TEACHING_TYPE_OPTIONS,
  TEACHING_TYPE_TO_LABEL,
  TeachingType,
} from '@/shared/educatorOptions';

import { FieldContainer, FieldLabel } from './Section';

/** What the About Me form holds while the user is editing. Labels, not stored values. */
export interface EducatorProfileForm {
  location: string;
  levels: string[];
  teachingType: string;
}

const controlBase = {
  width: '100%',
  borderRadius: '8px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '#d1d5db',
  background: '#FFF',
  fontSize: '16px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  fontWeight: 400,
};

/**
 * `select.control` styles the closed control and `select.container` the open drop. Both are read by
 * Select and SelectMultiple alike (`StyledSelect.StyledContainer`) — grommet's `selectMultiple`
 * theme key carries nothing but `maxInline`, so there is no separate place to style the multi.
 */
const selectTheme = {
  select: {
    container: { extend: { width: '100%' } },
    control: { extend: controlBase },
  },
};

const multiSelectTheme = {
  select: {
    container: {
      extend: {
        width: '100%',
        // The app theme paints a selected option's label white (`grommetTheme.js`), which reads
        // correctly in a single Select because the selected row is filled green behind it.
        // SelectMultiple marks selection with a checkbox and leaves the row white, so inheriting
        // that rule makes the chosen levels disappear. Back to the colour of every other row.
        button: {
          '&[aria-selected="true"]:not(:hover)': { color: 'black' },
        },
        // Grommet renders a "2 selected of 15 / Clear All" summary as the drop's first child and
        // offers no prop to suppress it. The design has no such control, and "Select All" is a
        // strange thing to offer someone naming the levels they teach.
        '> div:first-child': { display: 'none' },
      },
    },
    control: { extend: controlBase },
  },
};

/**
 * Collapses a multi-select into the summary the design shows. Levels are listed in the picker's
 * order rather than the order they were clicked, so the same set always reads the same way.
 */
const summarise = (selected: string[], maxVisible: number) => {
  if (selected.length === 0) return '';
  const ordered = LEVEL_OPTIONS.filter((option) => selected.includes(option));
  if (ordered.length <= maxVisible) return ordered.join(', ');
  return `${ordered.slice(0, maxVisible).join(', ')} (+ ${ordered.length - maxVisible} more)`;
};

/** The stored Educator Profile as form labels, with the location fallbacks applied. */
export const toEducatorProfileForm = (
  educatorDetails: { location?: ISOAlpha2Code; grades?: GradeNumber[]; teachingType?: TeachingType } | undefined,
  registrationCountryCode: string | undefined,
): EducatorProfileForm => ({
  // A saved answer always wins. Failing that we guess from where they registered — geoip from
  // signup day, which is the only country signal that exists on this page.
  location: ISO_TO_COUNTRY[educatorDetails?.location ?? ''] || ISO_TO_COUNTRY[registrationCountryCode ?? ''] || '',
  levels: (educatorDetails?.grades ?? []).map((level) => NUMBER_TO_LEVEL[level]).filter(Boolean),
  teachingType: educatorDetails?.teachingType ? TEACHING_TYPE_TO_LABEL[educatorDetails.teachingType] : '',
});

/** The form's labels back as stored values. Only answered fields are included. */
export const fromEducatorProfileForm = (form: EducatorProfileForm) => ({
  ...(form.location && { location: COUNTRY_TO_ISO[form.location] as ISOAlpha2Code }),
  ...(form.levels.length && {
    grades: LEVEL_OPTIONS.filter((option) => form.levels.includes(option)).map(
      (label) => LEVEL_TO_NUMBER[label],
    ) as GradeNumber[],
  }),
  ...(form.teachingType && { teachingType: LABEL_TO_TEACHING_TYPE[form.teachingType] }),
});

interface EducatorProfileFieldsProps {
  value: EducatorProfileForm;
  onChange: (value: EducatorProfileForm) => void;
  className?: string;
}

const EducatorProfileFields = ({ value, onChange, className }: EducatorProfileFieldsProps) => {
  const id = useId();
  const [countryOptions, setCountryOptions] = useState(COUNTRIES);

  const set = <K extends keyof EducatorProfileForm>(field: K, fieldValue: EducatorProfileForm[K]) =>
    onChange({ ...value, [field]: fieldValue });

  const onCountrySearch = (text: string) =>
    setCountryOptions(
      text === '' ? COUNTRIES : COUNTRIES.filter((country) => country.toLowerCase().includes(text.toLowerCase())),
    );

  return (
    <div className={className}>
      <FieldContainer className="desktop:max-w-[400px]">
        <FieldLabel htmlFor={`${id}-location`} className="text-base font-semibold text-[#162B1B]">
          Where are you based?
        </FieldLabel>
        <ThemeContext.Extend value={selectTheme}>
          <Select
            id={`${id}-location`}
            options={countryOptions}
            value={value.location}
            onChange={({ value: country }: { value: string }) => {
              set('location', country);
              setCountryOptions(COUNTRIES);
            }}
            onSearch={onCountrySearch}
            placeholder="Select location"
            searchPlaceholder="Search countries..."
            emptySearchMessage="No countries found"
            dropHeight="medium"
          />
        </ThemeContext.Extend>
      </FieldContainer>

      <FieldContainer className="mt-5 desktop:max-w-[400px]">
        <FieldLabel htmlFor={`${id}-levels`} className="text-base font-semibold text-[#162B1B]">
          Which levels do you teach?
        </FieldLabel>
        <ThemeContext.Extend value={multiSelectTheme}>
          <SelectMultiple
            id={`${id}-levels`}
            options={LEVEL_OPTIONS}
            value={value.levels}
            onChange={({ value: levels }: { value: string[] }) => set('levels', levels)}
            placeholder="Select levels"
            showSelectedInline={false}
            messages={{ multiple: summarise(value.levels, 2) }}
            dropHeight="small"
          />
        </ThemeContext.Extend>
      </FieldContainer>

      <FieldContainer className="mt-5 desktop:max-w-[400px]">
        <FieldLabel htmlFor={`${id}-teaching-type`} className="text-base font-semibold text-[#162B1B]">
          What type of teaching?
        </FieldLabel>
        <ThemeContext.Extend value={selectTheme}>
          <Select
            id={`${id}-teaching-type`}
            options={TEACHING_TYPE_OPTIONS}
            value={value.teachingType}
            onChange={({ value: teachingType }: { value: string }) => set('teachingType', teachingType)}
            placeholder="Select from Public, Homeschool..."
          />
        </ThemeContext.Extend>
      </FieldContainer>
    </div>
  );
};

export default EducatorProfileFields;
